import { Fragment, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { SearchSelect, type SSGroup } from "../components/ui/SearchSelect";
import { ToggleSwitch } from "../components/ui/ToggleSwitch";
import { Icon } from "../components/ui/Icon";
import {
  KIND_ETIKET, KIND_SIRASI, SINIF_SIRASI, SINIF_ETIKET, SINIF_TANIM, LOT_DURUM_ETIKET, MALIYET_NOTU_ETIKET, TUR_ATANMAMIS,
  suzgecOzeti, gorunurSiniflar, grupAdi, gruplariSirala, downloadStockReport, raporUyarilari, maliyetKaynagiMetni,
  raporGunMetni, raporZamanMetni,
  type StockReportData, type StockReportRow, type StockReportLot, type ReportKind, type LotSinifi,
} from "../lib/stockReportXlsx";
import "./StockReportPrint.css";

/**
 * ENVANTER RAPORU — ekran önizleme + A4 (yatay) baskı + Excel; "Ürün bazında" (lotlar birleşik) ve "Lot bazında".
 *
 * Stok/Lotlar > "Stok Raporu" yeni sekmede açar. URL yalnız BAŞLANGIÇ değerini verir (DeliveryPlanPrint kalıbı);
 * sonrasında süzgeçler bu ekranın state'idir. Varsayılan: Mamul + Ürün bazında + Yalnız stokta (müşterinin ana
 * ihtiyacı: mamul stoğunda elde olanı ayırmak). URL: ?gorunum=lot&kinds=finished,semi&itemTypeIds=a,b
 * &turAtanmamis=1&sadeceStokta=0&siniflar=saglam,skt
 *
 * AYNI VERİ: ekran, baskı ve Excel `inventory.stockReport`un TEK yanıtından çizilir — Excel ikinci istek atmaz,
 * ekranda görünenin kendisini dosyaya döker. Süzgeç değişip yeni yanıt gelene kadar Excel/Yazdır kapalıdır
 * (eski yanıt yeni süzgeç adıyla basılmasın). Künyedeki süzgeç metni de sunucunun YANKILADIĞI süzgeçtir.
 *
 * Lokasyon süzgeci BİLEREK yok: canlıda mamul stoğunun tamamı üretim hattı lokasyonlarında; "Mamul Deposu"
 * seçimi her şeyi sessizce gizlerdi. Lot satırında lokasyon yalnız bilgi olarak yazılır.
 */

const TUR_YOK = "__tur_atanmamis__";
const SINIF_IKON: Record<LotSinifi, string> = { saglam: "check", skt: "clock", blokeli: "lock", acik: "alertTriangle" };
const SINIF_TON: Record<LotSinifi, "ok" | "wrn" | "dgr"> = { saglam: "ok", skt: "dgr", blokeli: "wrn", acik: "dgr" };
/** Sınıf sütununda sıfırdan farklı değer tonu (ürün görünümü) — sağlam nötr, diğerleri dikkat çeker. */
const SINIF_HUCRE: Record<LotSinifi, string> = { saglam: "", skt: "dgr", blokeli: "wrn", acik: "neg" };

/** URL listesi "a,b" → geçerli değerler. Parametre YOKSA null (varsayılan uygulanır); boş/yanlışsa []. */
function urlListe<T extends string>(v: string | null, gecerli: readonly T[]): T[] | null {
  if (v == null) return null;
  return v.split(",").map((s) => s.trim()).filter((s): s is T => (gecerli as readonly string[]).includes(s));
}

const nf = (n: number) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 4 }).format(n);
const pf = (n: number) => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
/** Birim maliyet — 2 hane, gerekiyorsa 6'ya kadar (fiyat listesi Decimal(18,6)). */
const bf = (n: number) => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(n);
/** Tarih/zaman — Excel ile AYNI biçimleyici (Europe/Istanbul takvim günü): ekran 14.10 derken dosya 13.10 demesin. */
const gf = raporGunMetni;
const zf = raporZamanMetni;

function SinifRozeti({ s }: { s: LotSinifi }) {
  return (
    <span className={`srp-badge ${SINIF_TON[s]}`}>
      <Icon name={SINIF_IKON[s]} size={11} />{SINIF_ETIKET[s]}
    </span>
  );
}

/** Miktar hücresi: sıfır soluk "—", eksi kırmızı; değer mono + tabular. */
function MiktarTd({ v, ton = "" }: { v: number; ton?: string }) {
  if (v === 0) return <td className="n bos">—</td>;
  return <td className={`n ${v < 0 ? "neg" : ton}`}>{nf(v)}</td>;
}

export function StockReportPrintPage() {
  const [sp] = useSearchParams();
  // URL yalnız BAŞLANGIÇ değerini verir; sonrasında state kullanıcıya aittir.
  const [gorunum, setGorunum] = useState<"urun" | "lot">(sp.get("gorunum") === "lot" ? "lot" : "urun");
  const [kinds, setKinds] = useState<ReportKind[]>(() => urlListe(sp.get("kinds"), KIND_SIRASI) ?? ["finished"]);
  const [turIds, setTurIds] = useState<string[]>(() => (sp.get("itemTypeIds") ?? "").split(",").map((s) => s.trim()).filter(Boolean));
  const [turAtanmamis, setTurAtanmamis] = useState(sp.get("turAtanmamis") === "1");
  const [sadeceStokta, setSadeceStokta] = useState(sp.get("sadeceStokta") !== "0");
  const [siniflar, setSiniflar] = useState<LotSinifi[]>(() => urlListe(sp.get("siniflar"), SINIF_SIRASI) ?? [...SINIF_SIRASI]);

  const girdi = useMemo(() => ({
    gorunum,
    kinds: KIND_SIRASI.filter((k) => kinds.includes(k)),
    itemTypeIds: turIds.length ? turIds : undefined,
    turAtanmamis,
    sadeceStokta,
    siniflar: SINIF_SIRASI.filter((s) => siniflar.includes(s)),
  }), [gorunum, kinds, turIds, turAtanmamis, sadeceStokta, siniflar]);
  // Süzgeç değişince eski yanıt ekranda kalır (zıplama yok) ama Excel/Yazdır yeni yanıt gelene kadar kapalı.
  const raporQ = trpc.inventory.stockReport.useQuery(girdi, { placeholderData: (onceki) => onceki, refetchOnWindowFocus: false });
  const d = raporQ.data;
  const guncel = !!d && !raporQ.isFetching && !raporQ.isPlaceholderData;

  const turlerQ = trpc.settings.itemType.list.useQuery({ includeInactive: false }, { staleTime: 5 * 60_000 });
  const turSecenekleri = useMemo<SSGroup[]>(() => {
    const gruplar: SSGroup[] = [{ group: "Özel", items: [{ value: TUR_YOK, label: TUR_ATANMAMIS, desc: "Kart türü boş kartlar" }] }];
    for (const k of KIND_SIRASI) {
      const items = (turlerQ.data ?? []).filter((t) => t.kind === k).map((t) => ({ value: t.id, label: t.name, tail: t.code }));
      if (items.length) gruplar.push({ group: KIND_ETIKET[k], items });
    }
    return gruplar;
  }, [turlerQ.data]);
  const turSecili = useMemo(() => [...turIds, ...(turAtanmamis ? [TUR_YOK] : [])], [turIds, turAtanmamis]);

  const profQ = trpc.settings.companyProfile.get.useQuery({}, { staleTime: 5 * 60_000 });
  const prof = profQ.data;

  const [excelYuk, setExcelYuk] = useState(false);
  const [excelDurum, setExcelDurum] = useState<{ ton: "ok" | "hata"; metin: string } | null>(null);
  const excelIndir = async () => {
    if (!d || !guncel) return;
    setExcelYuk(true);
    setExcelDurum(null);
    try {
      const ad = await downloadStockReport(d); // ekrandaki yanıtın KENDİSİ — ikinci istek yok
      setExcelDurum({ ton: "ok", metin: `İndirildi: ${ad}` });
    } catch (e) {
      setExcelDurum({ ton: "hata", metin: `Excel üretilemedi: ${(e as Error).message}` });
    } finally {
      setExcelYuk(false);
    }
  };
  const kapat = () => {
    window.close();
    // Sekme betikle açılmadıysa (adres çubuğu / yer imi) close() bir şey yapmaz → Stok/Lotlar'a dönülür.
    window.setTimeout(() => { if (!window.closed) window.location.assign("/inventory"); }, 200);
  };
  const degistir = <T,>(liste: T[], x: T) => (liste.includes(x) ? liste.filter((y) => y !== x) : [...liste, x]);

  return (
    <div className="srp-root">
      {/* @page bileşen İÇİNDE: sayfa CSS'leri global yüklendiğinden .css'e yazılırsa diğer baskı sayfalarını da yatay yapardı. */}
      <style>{"@page { size: A4 landscape; margin: 10mm; }"}</style>

      <div className="srp-toolbar">
        <div className="srp-filt">
          <div className="srp-fld">
            <span className="srp-lbl" id="srp-gor">Görünüm</span>
            <div className="srp-seg" role="group" aria-labelledby="srp-gor">
              <button type="button" className={`btn-md ${gorunum === "urun" ? "primary" : "secondary"}`} aria-pressed={gorunum === "urun"} onClick={() => setGorunum("urun")}>Ürün bazında</button>
              <button type="button" className={`btn-md ${gorunum === "lot" ? "primary" : "secondary"}`} aria-pressed={gorunum === "lot"} onClick={() => setGorunum("lot")}>Lot bazında</button>
            </div>
          </div>
          <div className="srp-fld">
            <span className="srp-lbl" id="srp-tur">Tür</span>
            <div className="srp-chips" role="group" aria-labelledby="srp-tur">
              {KIND_SIRASI.map((k) => {
                const on = kinds.includes(k);
                return (
                  <button key={k} type="button" className={`btn-md ${on ? "primary" : "secondary"}`} aria-pressed={on} onClick={() => setKinds((p) => degistir(p, k))}>
                    {on ? <Icon name="check" size={13} /> : null}{KIND_ETIKET[k]}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="srp-fld srp-fld-tur">
            <span className="srp-lbl">Kart Türü</span>
            <SearchSelect
              value={null}
              multiValues={turSecili}
              onChangeMulti={(v) => { setTurAtanmamis(v.includes(TUR_YOK)); setTurIds(v.filter((x) => x !== TUR_YOK)); }}
              onChange={() => {}}
              options={turSecenekleri}
              placeholder="Tüm kart türleri"
              searchPlaceholder="Kart türü ara…"
              emptyText="Kart türü bulunamadı"
              multiSummary={(n) => `${n} kart türü seçili`}
            />
          </div>
          <div className="srp-fld">
            <span className="srp-lbl">Stok</span>
            <div className="srp-sw">
              <ToggleSwitch checked={sadeceStokta} onChange={setSadeceStokta} label="Yalnız stokta" />
            </div>
          </div>
          <div className="srp-fld">
            <span className="srp-lbl" id="srp-sinif">Sınıf</span>
            <div className="srp-chips" role="group" aria-labelledby="srp-sinif">
              {SINIF_SIRASI.map((s) => {
                const on = siniflar.includes(s);
                return (
                  <button key={s} type="button" className={`btn-md ${on ? "primary" : "secondary"}`} aria-pressed={on} onClick={() => setSiniflar((p) => degistir(p, s))}>
                    <Icon name={SINIF_IKON[s]} size={13} />{SINIF_ETIKET[s]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="srp-actions">
          {raporQ.isFetching ? <span className="srp-durum" role="status">Güncelleniyor…</span> : null}
          {excelDurum ? <span className={`srp-durum ${excelDurum.ton}`} role="status">{excelDurum.metin}</span> : null}
          <button type="button" className="btn-md secondary" onClick={kapat}><Icon name="x" size={14} />Kapat</button>
          <button type="button" className="btn-md secondary" onClick={() => void excelIndir()} disabled={!guncel || excelYuk}>
            <Icon name="sheet" size={14} />{excelYuk ? "Hazırlanıyor…" : "Excel İndir"}
          </button>
          <button type="button" className="btn-md primary" onClick={() => window.print()} disabled={!guncel}><Icon name="printer" size={14} />Yazdır</button>
        </div>
      </div>

      <div className="srp-page">
        {raporQ.isLoading ? (
          <div className="srp-empty">Yükleniyor…</div>
        ) : raporQ.isError && !d ? (
          <div className="srp-note dgr">Rapor yüklenemedi: {raporQ.error?.message}</div>
        ) : d ? (
          <RaporGovdesi d={d} sirketAdi={prof?.name ?? null} adres={prof?.address ?? null} logo={prof?.logoUrl ?? null} hata={raporQ.isError ? raporQ.error?.message ?? "" : null} />
        ) : null}
      </div>
    </div>
  );
}

function RaporGovdesi({ d, sirketAdi, adres, logo, hata }: { d: StockReportData; sirketAdi: string | null; adres: string | null; logo: string | null; hata: string | null }) {
  const lotGorunumu = d.suzgec.gorunum === "lot";
  const siniflar = gorunurSiniflar(d);
  const uyarilar = raporUyarilari(d); // Excel'in Genel Durum sayfasıyla AYNI liste

  return (
    <>
      <div className="srp-head">
        <div className="srp-head-top">
          <div>
            <div className="srp-title">Envanter Raporu</div>
            <div className="srp-sub">{lotGorunumu ? "Lot bazında — ürünler lotlarına ayrılmış" : "Ürün bazında — lotlar birleşik"}</div>
          </div>
          <div className="srp-co">
            <div>
              <b>{sirketAdi || "STEPERP"}</b>
              {adres ? <div>{adres}</div> : null}
            </div>
            {logo ? <img className="srp-logo" src={logo} alt={sirketAdi ?? "Logo"} /> : null}
          </div>
        </div>
        <div className="srp-meta">
          <span>Tesis: <b>{d.facilityName}</b></span>
          <span>Tarih: <b className="srp-mono">{zf(d.generatedAt)}</b></span>
          <span>Hazırlayan: <b>{d.generatedBy}</b></span>
        </div>
        <div className="srp-meta">
          {suzgecOzeti(d.suzgec).map((s) => <span key={s.etiket}>{s.etiket}: <b>{s.deger}</b></span>)}
        </div>
      </div>

      {hata ? <div className="srp-note dgr">Süzgeç güncellenemedi, ekrandaki rapor ÖNCEKİ süzgece aittir: {hata}</div> : null}
      {uyarilar.map((u, i) => (
        <div key={i} className={`srp-note${u.ton === "dgr" ? " dgr" : ""}`} role={u.ton === "dgr" ? "alert" : undefined}>{u.metin}</div>
      ))}

      <div className="srp-kpis">
        <div className="srp-kpi"><div className="l">Ürün</div><div className="v">{nf(d.ozet.urunSayisi)}</div></div>
        <div className="srp-kpi">
          {/* "Lot" = artı bakiyeli lot (sütun + ara toplamla aynı); kayıt açığı lotu ayrı etiketle. */}
          <div className="l">Lot</div><div className="v">{nf(d.ozet.lotSayisi)}</div>
          {d.ozet.acikLotSayisi > 0 ? <div className="s">+ {nf(d.ozet.acikLotSayisi)} kayıt açığı lotu</div> : null}
        </div>
        <div className="srp-kpi">
          <div className="l">Stok Değeri</div>
          <div className="v">{d.ozet.degerBilinmeyenLot > 0 ? "≥ " : ""}{pf(d.ozet.toplamDeger)}<span className="u">₺</span></div>
          {d.ozet.degerBilinmeyenLot > 0 ? <div className="s">değeri bilinen lotlar</div> : null}
        </div>
        <div className={`srp-kpi${d.ozet.degerBilinmeyenLot > 0 ? " wrn" : ""}`}>
          <div className="l">Değeri Bilinmeyen Lot</div><div className="v">{nf(d.ozet.degerBilinmeyenLot)}</div>
        </div>
      </div>

      {/* Birim bazında toplam — karışık birimler TOPLANMAZ (kart = tek birim). */}
      <div className="srp-sec">Birim Bazında Toplam</div>
      <div className="srp-scroll">
        <table className="srp-t">
          <thead>
            <tr>
              <th>Birim</th>
              <th className="n">Ürün</th>
              {siniflar.map((s) => <th key={s} className="n">{SINIF_ETIKET[s]}</th>)}
              <th className="n">Net</th>
              <th className="n">Değeri Bilinmeyen</th>
            </tr>
          </thead>
          <tbody>
            {d.ozet.birimler.length === 0 ? (
              <tr><td colSpan={4 + siniflar.length} className="bos">Süzgece uyan stok yok.</td></tr>
            ) : d.ozet.birimler.map((b) => (
              <tr key={b.uom}>
                <td><b>{b.uom}</b></td>
                <td className="n">{nf(b.urun)}</td>
                {siniflar.map((s) => <MiktarTd key={s} v={b[s]} ton={SINIF_HUCRE[s]} />)}
                <MiktarTd v={b.net} />
                <MiktarTd v={b.degerBilinmeyen} ton="wrn" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="srp-sec">{lotGorunumu ? "Lotlar" : "Ürünler"}</div>
      {d.rows.length === 0 ? (
        <div className="srp-empty">
          {d.suzgec.kinds.length === 0 ? "En az bir tür seçin." : d.suzgec.siniflar.length === 0 ? "En az bir sınıf seçin."
            : d.yalnizAcik.kartSayisi > 0 ? "Stokta ürün yok — yalnız kayıt açığı olan kartlar yukarıdaki uyarıda." : "Süzgece uyan ürün yok."}
        </div>
      ) : lotGorunumu ? <LotTablosu d={d} /> : <UrunTablosu d={d} siniflar={siniflar} />}

      <div className="srp-legend">
        {siniflar.map((s) => <div key={s}><b>{SINIF_ETIKET[s]}:</b> {SINIF_TANIM[s]}</div>)}
        {d.suzgec.sadeceStokta ? (
          <div><b>Yalnız stokta:</b> seçili sınıflarda artı bakiyeli lotu olan kartlar; bu kartların kayıt açığı lotları da gösterilir. Yalnız kayıt açığı kalan kartlar listelenmez, sayıları uyarıda.</div>
        ) : null}
        <div><b>Maliyet tabanı:</b> {d.maliyetTabani}</div>
        <div><b>Kapsam:</b> stok tür ve kart türüne göre süzülür; lokasyon yalnız bilgi amaçlıdır (üretim hattında duran mamul de dahildir).</div>
      </div>
    </>
  );
}

/** Ürün bazında: kart türü grupları + grup ara toplamı (birim bazında) + genel toplam (birim bazında). */
function UrunTablosu({ d, siniflar }: { d: StockReportData; siniflar: LotSinifi[] }) {
  const gruplar = useMemo(() => {
    const m = new Map<string, StockReportRow[]>();
    for (const r of d.rows) m.set(grupAdi(r.groupName), [...(m.get(grupAdi(r.groupName)) ?? []), r]);
    return gruplariSirala(m.keys()).map((g) => ({ ad: g, rows: m.get(g)! }));
  }, [d.rows]);
  const sutun = 7 + siniflar.length;
  const araToplam = (rows: StockReportRow[]) => {
    const m = new Map<string, { uom: string; s: Record<LotSinifi, number>; net: number; lot: number; deger: number; bilinmeyen: number }>();
    for (const r of rows) {
      const o = m.get(r.uom) ?? { uom: r.uom, s: { saglam: 0, skt: 0, blokeli: 0, acik: 0 }, net: 0, lot: 0, deger: 0, bilinmeyen: 0 };
      for (const s of SINIF_SIRASI) o.s[s] += r[s];
      o.net += r.onHand; o.lot += r.lotCount; o.deger += r.stockValue; o.bilinmeyen += r.degerBilinmeyen;
      m.set(r.uom, o);
    }
    const yuv = (n: number) => Math.round(n * 1e4) / 1e4;
    return [...m.values()].sort((a, b) => a.uom.localeCompare(b.uom, "tr")).map((o) => ({
      ...o, s: { saglam: yuv(o.s.saglam), skt: yuv(o.s.skt), blokeli: yuv(o.s.blokeli), acik: yuv(o.s.acik) },
      net: yuv(o.net), deger: Math.round(o.deger * 100) / 100, bilinmeyen: yuv(o.bilinmeyen),
    }));
  };
  return (
    <div className="srp-scroll">
      <table className="srp-t genis">
        <thead>
          <tr>
            <th>Ürün</th>
            <th>Birim</th>
            {siniflar.map((s) => <th key={s} className="n">{SINIF_ETIKET[s]}</th>)}
            <th className="n">Net</th>
            <th className="n">Lot</th>
            <th className="n">En Yakın SKT</th>
            <th className="n">Stok Değeri ₺</th>
            <th className="n">Değeri Bilinmeyen</th>
          </tr>
        </thead>
        <tbody>
          {gruplar.map((g) => (
            <Fragment key={g.ad}>
              <tr className="srp-grp"><td colSpan={sutun}>{g.ad}<span className="srp-gs">{nf(g.rows.length)} ürün</span></td></tr>
              {g.rows.map((r) => (
                <tr key={`${r.kind}:${r.id}`}>
                  <td><span className="srp-ad">{r.name}</span><span className="srp-kod">{r.code} · {KIND_ETIKET[r.kind]}</span></td>
                  <td>{r.uom}</td>
                  {siniflar.map((s) => <MiktarTd key={s} v={r[s]} ton={SINIF_HUCRE[s]} />)}
                  <MiktarTd v={r.onHand} />
                  <td className={`n${r.lotCount ? "" : " bos"}`}>{r.lotCount ? nf(r.lotCount) : "—"}</td>
                  <td className={`n${r.nearestExpiry && Date.parse(r.nearestExpiry) <= Date.now() ? " dgr" : ""}`}>{gf(r.nearestExpiry)}</td>
                  <td className={`n${r.stockValue ? "" : " bos"}`}>{r.stockValue ? pf(r.stockValue) : "—"}</td>
                  <MiktarTd v={r.degerBilinmeyen} ton="wrn" />
                </tr>
              ))}
              {araToplam(g.rows).map((t) => (
                <tr key={`ara-${g.ad}-${t.uom}`} className="srp-ara">
                  <td>Ara toplam</td>
                  <td>{t.uom}</td>
                  {siniflar.map((s) => <MiktarTd key={s} v={t.s[s]} />)}
                  <MiktarTd v={t.net} />
                  <td className="n">{nf(t.lot)}</td>
                  <td />
                  <td className="n">{pf(t.deger)}</td>
                  <MiktarTd v={t.bilinmeyen} ton="wrn" />
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          {d.ozet.birimler.map((b, i) => (
            <tr key={b.uom}>
              <td>{i === 0 ? "GENEL TOPLAM" : ""}</td>
              <td>{b.uom}</td>
              {siniflar.map((s) => <td key={s} className="n">{nf(b[s])}</td>)}
              <td className="n">{nf(b.net)}</td>
              <td className="n" />
              <td />
              <td className="n">{i === 0 ? `${d.ozet.degerBilinmeyenLot > 0 ? "≥ " : ""}${pf(d.ozet.toplamDeger)}` : ""}</td>
              <td className="n">{b.degerBilinmeyen ? nf(b.degerBilinmeyen) : "—"}</td>
            </tr>
          ))}
        </tfoot>
      </table>
    </div>
  );
}

/** Lot bazında: ürün başlığı → lotları (FEFO sırası) → ürün toplamı (ürün satırının kendisi: Σ lot = ürün). */
function LotTablosu({ d }: { d: StockReportData }) {
  const urunler = useMemo(() => {
    const satir = new Map(d.rows.map((r) => [`${r.kind}:${r.id}`, r]));
    const out: Array<{ urun: StockReportRow; lotlar: StockReportLot[] }> = [];
    for (const l of d.lotlar) {
      const key = `${l.itemKind}:${l.itemId}`;
      const son = out[out.length - 1];
      if (son && `${son.urun.kind}:${son.urun.id}` === key) { son.lotlar.push(l); continue; }
      const urun = satir.get(key);
      if (urun) out.push({ urun, lotlar: [l] });
    }
    return out;
  }, [d.rows, d.lotlar]);
  const SUTUN = 9;
  return (
    <div className="srp-scroll">
      <table className="srp-t genis">
        <thead>
          <tr>
            <th>Lot</th>
            <th>Üretim</th>
            <th>SKT</th>
            <th>Sınıf</th>
            <th>Lokasyon</th>
            <th className="n">Miktar</th>
            <th className="n">Birim Maliyet ₺</th>
            <th className="n">Değer ₺</th>
            <th>İlişkili Sipariş</th>
          </tr>
        </thead>
        <tbody>
          {urunler.map(({ urun, lotlar }) => (
            <Fragment key={`${urun.kind}:${urun.id}`}>
              <tr className="srp-grp">
                <td colSpan={SUTUN}>
                  <span className="srp-mono">{urun.code}</span> · {urun.name}
                  <span className="srp-gs">
                    {grupAdi(urun.groupName)} · {KIND_ETIKET[urun.kind]} · {nf(urun.lotCount)} lot
                    {urun.acikLotCount ? ` · ${nf(urun.acikLotCount)} kayıt açığı lotu` : ""} · birim {urun.uom}
                  </span>
                </td>
              </tr>
              {lotlar.map((l) => (
                <tr key={l.lotId}>
                  <td>
                    <span className="srp-mono">{l.code}</span>
                    {l.origin === "opening_stock" ? <span className="srp-acilis" title="Sayım öncesi mevcut stok">Açılış</span> : null}
                  </td>
                  <td className="srp-mono">{gf(l.producedAt)}</td>
                  <td className={`srp-mono${l.sinif === "skt" ? " dgr" : ""}`}>{gf(l.expiresAt)}</td>
                  <td>
                    <SinifRozeti s={l.sinif} />
                    {l.status !== "released" ? <span className="srp-durum-alt">{LOT_DURUM_ETIKET[l.status]}</span> : null}
                  </td>
                  <td>
                    {l.lokasyonlar.map((k) => (
                      <span key={k.kod} className="srp-sip">{k.ad}{l.lokasyonlar.length > 1 ? <span className="srp-mono"> ({nf(k.qty)})</span> : null}</span>
                    ))}
                  </td>
                  <td className={`n${l.qty < 0 ? " neg" : ""}`}>{nf(l.qty)}<span className="srp-u">{l.uom}</span></td>
                  <td className={`n${l.birimMaliyet == null ? " bos" : ""}`} title={l.qty > 0 ? maliyetKaynagiMetni(l) : undefined}>
                    {l.birimMaliyet == null ? "—" : bf(l.birimMaliyet)}
                    {/* Lotun kendi maliyeti kullanılmadıysa NEDENİ basılır (baskıda title görünmez). */}
                    {l.lotMaliyetiNotu ? <span className="srp-durum-alt">{MALIYET_NOTU_ETIKET[l.lotMaliyetiNotu]}</span> : null}
                  </td>
                  <td className={`n${l.deger == null ? (l.qty > 0 ? " wrn" : " bos") : ""}`}>
                    {l.deger != null ? pf(l.deger) : l.qty > 0 ? "bilinmiyor" : "—"}
                  </td>
                  <td>
                    {l.siparisler.length === 0 ? <span className="srp-sip">—</span> : l.siparisler.map((s) => (
                      <span key={`${s.iliski}-${s.code}`} className="srp-sip">
                        {s.iliski === "sevk" ? <span className="srp-sip-sevk">Sevk:</span> : null}
                        <span className="srp-mono">{s.code}</span>{s.musteri ? ` · ${s.musteri}` : ""}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
              <tr className="srp-ara">
                <td colSpan={5}>
                  Ürün toplamı
                  <span className="srp-kirilim">
                    {" "}· {SINIF_SIRASI.filter((s) => urun[s] !== 0).map((s) => `${SINIF_ETIKET[s]} ${nf(urun[s])}`).join(" · ") || "—"}
                  </span>
                </td>
                <td className={`n${urun.onHand < 0 ? " neg" : ""}`}>{nf(urun.onHand)}<span className="srp-u">{urun.uom}</span></td>
                <td />
                <td className={`n${urun.stockValue === 0 && urun.degerBilinmeyen > 0 ? " wrn" : ""}`}>
                  {/* Hiç değeri bilinen lot yoksa "0,00" YAZILMAZ — 0 "bedava" diye okunur. */}
                  {urun.stockValue === 0 && urun.degerBilinmeyen > 0 ? "bilinmiyor" : pf(urun.stockValue)}
                  {urun.degerBilinmeyen > 0 && urun.stockValue !== 0 ? <span className="srp-durum-alt">+ {nf(urun.degerBilinmeyen)} {urun.uom} değeri bilinmiyor</span> : null}
                </td>
                <td />
              </tr>
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          {d.ozet.birimler.filter((b) => b.net !== 0 || b.saglam || b.skt || b.blokeli || b.acik).map((b, i) => (
            <tr key={b.uom}>
              <td colSpan={5}>{i === 0 ? "GENEL TOPLAM" : ""}</td>
              <td className="n">{nf(b.net)}<span className="srp-u">{b.uom}</span></td>
              <td />
              <td className="n">{i === 0 ? `${d.ozet.degerBilinmeyenLot > 0 ? "≥ " : ""}${pf(d.ozet.toplamDeger)}` : ""}</td>
              <td />
            </tr>
          ))}
        </tfoot>
      </table>
    </div>
  );
}
